import { supabase } from "./supabase";
import { fixSupabasePublicStorageUrl } from "./supabaseUrl";

export const HOME_HERO_AD_BUCKET = "foreducator-media";
export const HOME_HERO_AD_WEEK_KEY = "fe-home-hero-ad-hide-until";
export const HOME_HERO_WIDE_AD_WEEK_KEY = "fe-home-hero-wide-ad-hide-until";
export const HOME_HERO_AD_HIDE_MS = 7 * 24 * 60 * 60 * 1000;

export type HomeHeroAdPlacement = "hero" | "wide" | "third";

export const HOME_HERO_AD_PLACEMENTS = [
  { value: "wide", label: "1열" },
  { value: "hero", label: "2열" },
  { value: "third", label: "3열" },
] as const;

export type ActiveHomeHeroAd = {
  kind: "image" | "adsense";
  placement: HomeHeroAdPlacement;
  imageUrl: string;
  linkUrl: string;
  altText: string;
  updatedAt: string;
  hideKey: string;
};

export function homeHeroAdPublicUrl(imagePath: string | null | undefined): string | null {
  // Upgrade this legacy landscape creative only; future admin uploads remain authoritative.
  if (imagePath === "home-hero-ad/0535db23-4e9f-4e4d-a15c-c14133f6ede2.png") return "/images/ads/class-assignment-portrait.webp";
  if (!imagePath || !supabase) return null;
  const url = supabase.storage.from(HOME_HERO_AD_BUCKET).getPublicUrl(imagePath).data.publicUrl;
  return url ? fixSupabasePublicStorageUrl(url) : null;
}

export function isHomeHeroAdHidden(version: string, now = Date.now(), storageKey = HOME_HERO_AD_WEEK_KEY): boolean {
  if (!version) return false;
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { until?: unknown; version?: unknown };
    if (parsed.version !== version || typeof parsed.until !== "number") return false;
    return parsed.until > now;
  } catch {
    return false;
  }
}

export function hideHomeHeroAdForWeek(version: string, now = Date.now(), storageKey = HOME_HERO_AD_WEEK_KEY): void {
  try {
    localStorage.setItem(storageKey, JSON.stringify({ until: now + HOME_HERO_AD_HIDE_MS, version }));
  } catch {
    // Closing still works when browser storage is unavailable.
  }
}

type HomeHeroAdRow = {
  id: string;
  kind?: string | null;
  placement?: string | null;
  image_path: string | null;
  link_url: string | null;
  alt_text: string | null;
  updated_at: string | null;
};

function toActiveAd(row: HomeHeroAdRow): ActiveHomeHeroAd | null {
  if (!row.id || !row.updated_at) return null;
  const placement: HomeHeroAdPlacement = row.placement === "third" ? "third" : row.placement === "wide" ? "wide" : "hero";
  const shared = {
    placement,
    altText: row.alt_text?.trim() || "광고",
    updatedAt: `${row.id}:${row.updated_at}`,
    hideKey: placement === "third" ? "fe-home-hero-third-ad-hide-until" : placement === "wide" ? HOME_HERO_WIDE_AD_WEEK_KEY : HOME_HERO_AD_WEEK_KEY,
  };
  if (row.kind === "adsense") {
    return { kind: "adsense", imageUrl: "", linkUrl: "", ...shared };
  }
  if (!row.image_path || !row.link_url) return null;
  const imageUrl = homeHeroAdPublicUrl(row.image_path);
  if (!imageUrl) return null;
  return { kind: "image", imageUrl, linkUrl: row.link_url, ...shared };
}

export async function fetchActiveHomeHeroAds(): Promise<{
  hero: ActiveHomeHeroAd | null;
  wide: ActiveHomeHeroAd | null;
  third: ActiveHomeHeroAd | null;
}> {
  if (!supabase) return { hero: null, wide: null, third: null };
  const { data, error } = await supabase
    .from("home_hero_ad")
    .select("id, kind, placement, image_path, link_url, alt_text, updated_at")
    .eq("is_active", true);

  if (error || !data) return { hero: null, wide: null, third: null };

  let hero: ActiveHomeHeroAd | null = null;
  let wide: ActiveHomeHeroAd | null = null;
  let third: ActiveHomeHeroAd | null = null;
  for (const row of data as HomeHeroAdRow[]) {
    const ad = toActiveAd(row);
    if (!ad) continue;
    if (row.placement === "third") third = ad;
    else if (row.placement === "wide") wide = ad;
    else hero = ad;
  }
  return { hero, wide, third };
}
